import { useState } from 'react';
import { Link } from 'react-router-dom';
import { melbourneDate } from '../../../shared/dates';
import { type SocialAssetWithProject } from '../../../shared/types';
import { formatDate } from '../../lib/calendar';
import { restrictionSource, safeUrl, shortUrl, type SocialActions } from '../../lib/socialCalendar';
import { FieldRow } from '../FieldSheet';
import { Sheet, SheetButton, SheetRow } from '../Sheet';
import { LostIcon, TrashIcon, XIcon } from '../icons';
import { StatusPill, assetContext } from './shared';

// ── Asset sheet (mockup §10.1 C) ─────────────────────────────────────────

export function AssetSheet({
  asset,
  actions,
  onSchedule,
  onClose,
}: {
  asset: SocialAssetWithProject;
  actions: SocialActions;
  onSchedule: () => void;
  onClose: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const source = restrictionSource(asset);
  const href = safeUrl(asset.url);
  const used = asset.status === 'used';

  return (
    <>
      <Sheet
        open
        onClose={onClose}
        variant="half"
        title={asset.label}
        context={assetContext(asset)}
        rightAction={<SheetButton onClick={onClose}>Close</SheetButton>}
        className="social-m-sheet"
      >
        {source !== null ? (
          // Restricted: the note replaces the field list — nothing here is
          // actionable until the block is cleared, and when it comes from the
          // project flag it can only be cleared over there.
          <div className="social-m-note restricted">
            <div className="k">
              {source === 'project'
                ? `restricted · project flag · ${asset.project_name ?? 'project'} · no social use`
                : 'restricted · this asset'}
            </div>
            <p>
              {(source === 'project' ? asset.project_social_restricted_note : asset.restricted_note)
                ?? 'No note recorded.'}
            </p>
            {source === 'project' && asset.project_id !== null && (
              <Link className="social-m-link" to={`/projects/${asset.project_id}`} onClick={onClose}>
                Clear the flag on the project →
              </Link>
            )}
            {source === 'asset' && (
              <div className="social-m-btns">
                <button
                  type="button"
                  className="cbtn wide pri"
                  onClick={() => {
                    onClose();
                    actions.onUnrestrict(asset);
                  }}
                >
                  Unrestrict
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <FieldRow label="Status" value={<StatusPill asset={asset} />} />
            <FieldRow
              label="Link"
              mono
              tone="acc"
              value={
                href !== null ? (
                  <a href={href} target="_blank" rel="noreferrer">
                    {shortUrl(href)} ↗
                  </a>
                ) : asset.url ? (
                  <span className="social-m-nolink">{asset.url} · not a web link</span>
                ) : (
                  '—'
                )
              }
            />
            <FieldRow
              label="Source"
              tone={asset.project_id !== null ? 'acc' : undefined}
              value={
                asset.project_id !== null ? (
                  <Link to={`/projects/${asset.project_id}`} onClick={onClose}>
                    {asset.project_name} →
                  </Link>
                ) : (
                  'Standalone'
                )
              }
            />
            <FieldRow label="Added" mono value={formatDate(melbourneDate(asset.created_at))} />
            <div className="social-m-note">
              <div className="k">Note</div>
              <p>{asset.note ?? '—'}</p>
            </div>
            <div className="social-m-btns">
              {used ? (
                <button
                  type="button"
                  className="cbtn wide pri"
                  onClick={() => {
                    onClose();
                    actions.onRepurpose(asset);
                  }}
                >
                  Repurpose
                </button>
              ) : (
                <button type="button" className="cbtn wide pri" onClick={onSchedule}>
                  Schedule →
                </button>
              )}
              <button
                type="button"
                className="cbtn wide"
                onClick={() => {
                  onClose();
                  actions.onEdit(asset);
                }}
              >
                Edit
              </button>
            </div>
            {asset.status === 'scheduled' && (
              // The library row now reads "IG · 14 Sep" where the desktop row
              // carried an "unschedule" button, so the sheet has to hold it —
              // the calendar's slot sheet is a different tab.
              <SheetRow
                icon={<XIcon />}
                label="Unschedule"
                onSelect={() => {
                  onClose();
                  actions.onUnschedule(asset);
                }}
              />
            )}
          </>
        )}
        {source !== 'project' && (
          <SheetRow
            icon={<LostIcon />}
            label={source === 'asset' ? 'Edit restriction…' : 'Restrict…'}
            tone="danger"
            onSelect={() => {
              onClose();
              actions.onRestrict(asset);
            }}
          />
        )}
        <SheetRow
          icon={<TrashIcon />}
          label="Delete asset"
          tone="danger"
          onSelect={() => setConfirmDelete(true)}
        />
      </Sheet>

      {confirmDelete && (
        <Sheet
          open
          onClose={() => setConfirmDelete(false)}
          variant="half"
          title="Delete asset?"
          context={asset.label}
          rightAction={<SheetButton onClick={() => setConfirmDelete(false)}>Cancel</SheetButton>}
          footer={
            <>
              <button type="button" className="cbtn wide" onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="cbtn wide dng"
                onClick={() => {
                  setConfirmDelete(false);
                  onClose();
                  actions.onDelete(asset);
                }}
              >
                Delete
              </button>
            </>
          }
        >
          <p className="social-m-confirm">
            The asset and its plan go for good. The file itself is untouched — this only removes the
            library row.
          </p>
        </Sheet>
      )}
    </>
  );
}
